/**
 * usePartySocket React Hook
 *
 * React hook for managing PartyKit WebSocket connections.
 * Provides connection state management, message sending/receiving,
 * and automatic integration with the game store.
 *
 * Features:
 * - Automatic connection management with lifecycle hooks
 * - Connection status tracking
 * - Message sending and receiving
 * - Automatic reconnection handling
 * - Integration with Zustand game store
 * - Error handling and callbacks
 *
 * Phase 5.1: PartyKit Client Integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createPartyConnection,
  disconnectParty,
  sendMessage as sendPartyMessage,
  getConnectionStatus,
  CONNECTION_STATUS,
} from '../services/partyService';
import { useGameStore } from '../stores/gameStore';

/**
 * usePartySocket Hook
 *
 * @param {string} roomId - The PartyKit room ID to connect to
 * @param {string} host - The PartyKit host URL
 * @param {object} options - Configuration options
 * @param {boolean} options.autoConnect - Whether to connect automatically on mount (default: true)
 * @param {boolean} options.reconnect - Whether to enable automatic reconnection (default: true)
 * @param {function} options.onMessage - Optional callback for received messages
 * @param {function} options.onConnect - Optional callback for connection established
 * @param {function} options.onDisconnect - Optional callback for disconnection
 * @param {function} options.onError - Optional callback for errors
 * @returns {object} Hook state and methods
 */
export function usePartySocket(roomId, host, options = {}) {
  // Validate required parameters
  if (!roomId || roomId.trim() === '') {
    throw new Error('Room ID is required');
  }

  if (!host || host.trim() === '') {
    throw new Error('Host is required');
  }

  // Destructure options with defaults
  const {
    autoConnect = true,
    reconnect = true,
    onMessage: onMessageCallback,
    onConnect: onConnectCallback,
    onDisconnect: onDisconnectCallback,
    onError: onErrorCallback,
  } = options;

  // State
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTION_STATUS.DISCONNECTED
  );
  const [error, setError] = useState(null);

  // Game store
  const handleWebSocketMessage = useGameStore(
    (state) => state.handleWebSocketMessage
  );

  // Refs to track callbacks (avoid recreating connection on callback changes)
  const onMessageRef = useRef(onMessageCallback);
  const onConnectRef = useRef(onConnectCallback);
  const onDisconnectRef = useRef(onDisconnectCallback);
  const onErrorRef = useRef(onErrorCallback);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessageCallback;
  }, [onMessageCallback]);

  useEffect(() => {
    onConnectRef.current = onConnectCallback;
  }, [onConnectCallback]);

  useEffect(() => {
    onDisconnectRef.current = onDisconnectCallback;
  }, [onDisconnectCallback]);

  useEffect(() => {
    onErrorRef.current = onErrorCallback;
  }, [onErrorCallback]);

  /**
   * Handle received messages
   */
  const handleMessage = useCallback(
    (message) => {
      console.log('[usePartySocket] Message received:', message.type);

      // Clear error on successful message
      if (message.type === 'CONNECTED') {
        setError(null);
        if (onConnectRef.current) {
          onConnectRef.current();
        }
      }

      // Handle error messages
      if (message.type === 'ERROR') {
        const errorMessage =
          message.message || message.error || 'Unknown error';
        setError(errorMessage);
        if (onErrorRef.current) {
          onErrorRef.current(errorMessage);
        }
      }

      // Forward message to game store
      handleWebSocketMessage(message);

      // Call optional callback
      if (onMessageRef.current) {
        onMessageRef.current(message);
      }
    },
    [handleWebSocketMessage]
  );

  /**
   * Connect to PartyKit room
   */
  const connect = useCallback(() => {
    console.log('[usePartySocket] Connecting to room:', roomId);

    try {
      // Don't connect if already connected
      const status = getConnectionStatus();
      if (status === CONNECTION_STATUS.CONNECTED) {
        console.log('[usePartySocket] Already connected');
        return;
      }

      createPartyConnection(roomId, host, handleMessage);
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);
    } catch (err) {
      console.error('[usePartySocket] Connection error:', err);
      setError(err.message);
      setConnectionStatus(CONNECTION_STATUS.ERROR);

      if (onErrorRef.current) {
        onErrorRef.current(err.message);
      }
    }
  }, [roomId, host, handleMessage]);

  /**
   * Disconnect from PartyKit room
   */
  const disconnect = useCallback(() => {
    console.log('[usePartySocket] Disconnecting');

    try {
      disconnectParty();
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);

      if (onDisconnectRef.current) {
        onDisconnectRef.current();
      }
    } catch (err) {
      console.error('[usePartySocket] Disconnect error:', err);
      setError(err.message);
    }
  }, []);

  /**
   * Send a message to the party
   */
  const sendMessage = useCallback((message) => {
    try {
      sendPartyMessage(message);
    } catch (err) {
      console.error('[usePartySocket] Send error:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Auto-connect on mount if enabled
   */
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  /**
   * Poll connection status periodically
   */
  useEffect(() => {
    const statusInterval = setInterval(() => {
      const status = getConnectionStatus();
      setConnectionStatus(status);
    }, 500);

    return () => {
      clearInterval(statusInterval);
    };
  }, []);

  /**
   * Derived state
   */
  const isConnected = connectionStatus === CONNECTION_STATUS.CONNECTED;
  const isConnecting = connectionStatus === CONNECTION_STATUS.CONNECTING;
  const isReconnecting = connectionStatus === CONNECTION_STATUS.RECONNECTING;

  return {
    // Connection state
    connectionStatus,
    isConnected,
    isConnecting,
    isReconnecting,
    error,

    // Methods
    connect,
    disconnect,
    sendMessage,
  };
}
